import { AfterContentInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { MapService } from '@maplibre/ngx-maplibre-gl';
import { CategoryControl } from './category-control';
import { IPOICategory } from '../../poi.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AlertButton, AlertInput, IonicModule } from '@ionic/angular';
import { IonAlertCustomEvent, OverlayEventDetail } from '@ionic/core';

@Component({
    selector: 'app-map-control-category',
    template: `
    <div class="maplibregl-ctrl maplibregl-ctrl-group category" #content>
      <button id="present-alert">
        <span id="category-label" style="display:none;">{{ 'map.category.selectionHeader' | translate }}</span>
        <ion-icon name="list-outline" size="large" aria-labelledby="category-label"></ion-icon>
      </button>
      <ion-alert
        trigger="present-alert"
        [header]="'map.category.selectionHeader' | translate"
        [buttons]="alertButtons"
        [inputs]="alertInputs"
        (didDismiss)="changed($event)"
      ></ion-alert>
    </div>
  `,
    styleUrls: ['./category.component.scss'],
    imports: [IonicModule, TranslatePipe]
})
export class CategoryControlComponent implements AfterContentInit, OnDestroy, OnChanges {
  private mapService = inject(MapService);
  translate = inject(TranslateService);

  @Input()
  public position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

  @Input()
  public categories: IPOICategory[] = [];

  @Input()
  public enabledCategories: number[] = [];

  @Output()
  public enabledCategoryIds: EventEmitter<number[]> = new EventEmitter();

  @ViewChild('content', { static: true }) content: ElementRef;

  private control: CategoryControl;

  public alertButtons: (string | AlertButton)[] = [
    {
      text: this.translate.instant('map.category.enableAll'),
      role: 'enable_all',
      handler: (): false => {
        this.setAlertInputs(this.categories.map((category) => category.id));
        return false;
      }
    },
    {
      text: this.translate.instant('map.category.disableAll'),
      role: 'disable_all',
      handler: (): false => {
        this.setAlertInputs([]);
        return false;
      }
    },
    'OK'
  ];

  public alertInputs: AlertInput[] = [];

  ngOnChanges(_: SimpleChanges): void {
    if(this.categories.length) {
      this.setAlertInputs(this.enabledCategories);
    }
  }

  ngAfterContentInit(): void {
    if (this.content.nativeElement.childNodes.length) {
      this.control = new CategoryControl(this.content.nativeElement);

      this.mapService.mapCreated$.subscribe((): void => {
        this.mapService.addControl(this.control, this.position);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.mapService.mapInstance.hasControl(this.control)) {
      this.mapService.removeControl(this.control);
    }
  }

  public getLanguageCode(): string {
    const defaultLangCode: string = (this.translate.getFallbackLang() ?? 'de-DE').split('-')[0] ?? null;
    const currentLang: string = (this.translate.currentLang() ?? 'de-DE').split('-')[0] ?? null;
    return currentLang ? currentLang : defaultLangCode ? defaultLangCode : 'de';
  }

  public changed(event: IonAlertCustomEvent<OverlayEventDetail>): void {
    if(!event.detail.data) {  // User has taped on the backdrop
      return this.setAlertInputs(this.enabledCategories);
    } else {
      this.enabledCategoryIds.emit(event.detail.data?.values);
    }
  }

  private setAlertInputs(categories: number[]): void {
    this.alertInputs = [];
    for(const category of this.categories) {
      this.alertInputs.push({
        value: category.id,
        label: category.name[this.getLanguageCode()],
        type: 'checkbox',
        checked: categories.includes(category.id)
      })
    }
  }
}
